/**
 * La forme des fichiers de l'archive d'export.
 *
 * Deux lecteurs, deux formats. Le patron ouvre ses données dans Excel le
 * lendemain de son départ : il lui faut du **CSV** qu'Excel en français lit
 * sans manipulation. Le logiciel qui le reprend ensuite a besoin des valeurs
 * exactes : il lui faut du **JSON**. Les deux sont écrits côte à côte, table
 * par table.
 *
 * Pur : ni lecture ni encodage. Les octets et les empreintes viennent de
 * l'appelant.
 */
import { projectSlug } from "../photos/types.js";
import {
  EXCLUDED_TABLES,
  EXPORTED_TABLES,
  SECRET_COLUMN_PATTERN,
} from "./tables.js";
import type { ExportFolder } from "./tables.js";

export type ExportRow = Record<string, unknown>;

// ─── Les lignes ──────────────────────────────────────────────────────────

/**
 * Ce qui sort d'une ligne : ses colonnes, moins celles que le classement
 * retire, et ses copies JSON débarrassées des clés qui ressemblent à un
 * secret.
 *
 * Le nettoyage des copies JSON double celui que la base fait depuis
 * `20260301005800` : une ligne écrite avant, ou par un chemin oublié demain,
 * ne doit pas suffire à faire partir un secret dans un `.zip`.
 */
export function redactRow(table: string, row: ExportRow): ExportRow {
  const spec = EXPORTED_TABLES[table];
  if (!spec) throw new Error(`Table non exportée : ${table}`);

  const out: ExportRow = {};
  for (const [column, value] of Object.entries(row)) {
    if (spec.omit && column in spec.omit) continue;
    out[column] = spec.scrubJson?.includes(column) ? scrubSecrets(value) : value;
  }
  return out;
}

function scrubSecrets(value: unknown): unknown {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      ([key]) => !SECRET_COLUMN_PATTERN.test(key),
    ),
  );
}

// ─── CSV ─────────────────────────────────────────────────────────────────

/**
 * Le BOM n'est pas décoratif : sans lui, Excel lit l'UTF-8 comme du
 * Windows-1252 et « Réfection » devient « RÃ©fection ».
 */
const BOM = "﻿";

/** Le point-virgule : avec la virgule, Excel en français met tout dans la colonne A. */
const SEPARATOR = ";";

/**
 * Une cellule qui commence par l'un de ces caractères est lue par Excel comme
 * une formule. Les noms de fournisseurs viennent de factures reçues — un
 * contenu extérieur — et `=HYPERLINK(…)` dans un nom ne doit rien exécuter
 * sur le poste du patron. L'apostrophe de tête est la parade usuelle.
 */
const FORMULA_START = /^[=+\-@\t\r]/;

function cell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "oui" : "non";
  // La virgule décimale, sans quoi Excel en français lit 12.5 comme du texte
  // — ou comme une date. Le JSON, lui, garde la valeur exacte.
  if (typeof value === "number") return String(value).replace(".", ",");

  const text = typeof value === "string" ? value : JSON.stringify(value);
  const guarded = FORMULA_START.test(text) ? `'${text}` : text;
  return /[;"\r\n]|^\s|\s$/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/**
 * Les colonnes d'une table, dans l'ordre de la première ligne. Une colonne
 * absente de la première ligne mais présente plus loin est ajoutée au bout,
 * plutôt que perdue.
 */
function columnsOf(rows: readonly ExportRow[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) for (const column of Object.keys(row)) seen.add(column);
  return [...seen];
}

export function toCsv(rows: readonly ExportRow[]): string {
  const columns = columnsOf(rows);
  if (columns.length === 0) return BOM;
  const lines = [
    columns.map(cell).join(SEPARATOR),
    ...rows.map((row) => columns.map((column) => cell(row[column])).join(SEPARATOR)),
  ];
  // CRLF : la fin de ligne qu'Excel et la RFC 4180 attendent.
  return BOM + lines.join("\r\n") + "\r\n";
}

export function toJson(rows: readonly ExportRow[]): string {
  return JSON.stringify(rows, null, 2) + "\n";
}

// ─── Les documents ───────────────────────────────────────────────────────

/** Les numéros des pièces, pour nommer les PDF comme le patron les connaît. */
export interface DocumentNumbers {
  quotes: ReadonlyMap<string, string>;
  invoices: ReadonlyMap<string, string>;
}

export function documentNumbers(
  quotes: readonly ExportRow[],
  invoices: readonly ExportRow[],
): DocumentNumbers {
  const index = (rows: readonly ExportRow[], column: string) =>
    new Map(
      rows
        .filter((row) => typeof row.id === "string" && typeof row[column] === "string")
        .map((row) => [row.id as string, row[column] as string]),
    );
  return { quotes: index(quotes, "quote_number"), invoices: index(invoices, "invoice_number") };
}

/** Un numéro de pièce peut porter « / » : ce serait un dossier dans l'archive. */
function safeName(value: string): string {
  return value.replace(/[\\/:*?"<>|]+/g, "-").trim() || "sans-numero";
}

/**
 * Où range-t-on un objet du bucket `documents` dans l'archive.
 *
 * Le Storage nomme les PDF par identifiant (`quotes/3f2a….pdf`) ; le patron,
 * lui, cherche `DEV-2026-0012`. Un document dont la pièce a disparu garde son
 * nom d'origine plutôt que d'être écarté.
 */
export function documentEntryName(
  storagePath: string,
  tenantId: string,
  numbers: DocumentNumbers,
): string {
  const relative = storagePath.startsWith(`${tenantId}/`)
    ? storagePath.slice(tenantId.length + 1)
    : storagePath;
  const [folder, ...rest] = relative.split("/");
  const file = rest.join("/");
  const dot = file.lastIndexOf(".");
  const stem = dot > 0 ? file.slice(0, dot) : file;
  const extension = dot > 0 ? file.slice(dot) : "";

  switch (folder) {
    case "quotes": {
      const number = numbers.quotes.get(stem);
      return `documents/devis/${number ? safeName(number) + extension : file}`;
    }
    case "invoices": {
      const number = numbers.invoices.get(stem);
      return `documents/factures/${number ? safeName(number) + extension : file}`;
    }
    case "einvoices": {
      const number = numbers.invoices.get(stem);
      return `documents/factur-x/${number ? safeName(number) + extension : file}`;
    }
    case "receipts":
      return `documents/justificatifs/${file}`;
    default:
      return `documents/${relative}`;
  }
}

/**
 * Deux entrées d'archive ne peuvent pas porter le même nom : la seconde
 * écraserait la première à l'extraction, sans un mot. On numérote.
 */
export function uniqueNames(names: readonly string[]): string[] {
  const used = new Set<string>();
  return names.map((name) => {
    let candidate = name;
    const dot = name.lastIndexOf(".");
    const stem = dot > name.lastIndexOf("/") ? name.slice(0, dot) : name;
    const extension = dot > name.lastIndexOf("/") ? name.slice(dot) : "";
    for (let n = 2; used.has(candidate); n++) candidate = `${stem}-${n}${extension}`;
    used.add(candidate);
    return candidate;
  });
}

// ─── L'archive ───────────────────────────────────────────────────────────

/** Le jour, à Paris, au format AAAA-MM-JJ : celui qu'affichera le nom du fichier. */
export function parisDay(at: Date): string {
  // `fr-CA` est la locale qui écrit les dates en AAAA-MM-JJ.
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/** `dker-export-maconnerie-durand-2026-10-05.zip` */
export function exportArchiveName(tenantName: string, at: Date): string {
  return `dker-export-${projectSlug(tenantName).toLowerCase()}-${parisDay(at)}.zip`;
}

export interface ManifestFile {
  path: string;
  bytes: number;
  sha256: string;
}

export interface ManifestTable {
  table: string;
  folder: ExportFolder;
  rows: number;
}

export interface ExportManifestInput {
  generatedAt: Date;
  tenant: { id: string; name: string };
  tables: readonly ManifestTable[];
  files: readonly ManifestFile[];
  /** Documents listés dans le Storage mais qu'il n'a pas rendus. */
  missingDocuments: readonly string[];
}

/**
 * Le manifeste : ce que l'archive contient, et de quoi vérifier qu'elle est
 * intacte. Il dit aussi ce qu'elle ne contient pas, et pourquoi — un départ
 * se passe mieux quand rien n'a l'air caché.
 */
export function manifestJson(input: ExportManifestInput): string {
  const omitted: Record<string, Record<string, string>> = {};
  for (const [table, spec] of Object.entries(EXPORTED_TABLES)) {
    if (spec.omit) omitted[table] = { ...spec.omit };
  }

  return (
    JSON.stringify(
      {
        format: "dker-export",
        version: 1,
        genere_le: input.generatedAt.toISOString(),
        entreprise: input.tenant,
        tables: input.tables.map((t) => ({ table: t.table, dossier: t.folder, lignes: t.rows })),
        fichiers: input.files.map((f) => ({ chemin: f.path, octets: f.bytes, sha256: f.sha256 })),
        documents_manquants: input.missingDocuments,
        colonnes_retirees: omitted,
        tables_non_exportees: { ...EXCLUDED_TABLES },
        photos:
          "Non incluses ici : chaque chantier a son propre bouton « Télécharger l'archive » " +
          "dans l'application, tant que le compte est ouvert.",
      },
      null,
      2,
    ) + "\n"
  );
}

const FOLDER_LABELS: Record<ExportFolder, string> = {
  entreprise: "la fiche de l'entreprise, ses réglages, ses utilisateurs et le journal d'audit",
  commercial: "clients, devis, factures, paiements, relances, catalogue",
  chantiers: "chantiers, phases, affectations, tâches, rendez-vous, remontées du terrain",
  equipe: "salariés, coûts horaires, heures, pointages, absences, habilitations, sécurité",
  achats: "fournisseurs, dépenses, factures reçues, commandes, mouvements de stock",
  "factur-x": "les transmissions de factures électroniques",
};

function count(n: number, word: string): string {
  return `${n} ${word}${n > 1 ? "s" : ""}`;
}

export function readmeText(input: {
  tenantName: string;
  generatedAt: Date;
  totalRows: number;
  documents: number;
}): string {
  const folders = (Object.keys(FOLDER_LABELS) as ExportFolder[])
    .map((folder) => `  ${folder}/ — ${FOLDER_LABELS[folder]}`)
    .join("\r\n");

  return [
    `Export des données de ${input.tenantName}`,
    `Produit par DKER le ${parisDay(input.generatedAt)} — ${count(input.totalRows, "ligne")}, ${count(input.documents, "document")}.`,
    "",
    "CE QUE CONTIENT CETTE ARCHIVE",
    "",
    folders,
    "  documents/ — les PDF des devis et des factures, les fichiers Factur-X, les justificatifs",
    "",
    "Chaque table existe en deux exemplaires :",
    "  .csv  — à ouvrir dans Excel ou LibreOffice (séparateur point-virgule, UTF-8).",
    "  .json — les valeurs exactes, pour un autre logiciel.",
    "",
    "Dans les CSV, les nombres sont écrits avec une virgule décimale, et oui/non",
    "remplace vrai/faux. Excel peut afficher un SIRET en notation scientifique :",
    "la valeur exacte est dans le fichier .json correspondant.",
    "",
    "CE QUI N'Y EST PAS",
    "",
    "  - Les photos de chantier : un bouton « Télécharger l'archive » par chantier,",
    "    dans l'application, tant que le compte est ouvert.",
    "  - Les clés d'accès aux services tiers (SUPER PDP, PISTE) : elles sont à",
    "    révoquer auprès de ces services, pas à conserver.",
    "  - Les données propres au fonctionnement de DKER. La liste exacte, avec la",
    "    raison de chaque exclusion, est dans manifeste.json.",
    "",
    "VÉRIFIER L'ARCHIVE",
    "",
    "manifeste.json donne, pour chaque fichier, sa taille et son empreinte SHA-256.",
    "",
  ].join("\r\n");
}
